<template>
    <template v-if="spinMode===true">
        <input type="number"
               :value="value"
               v-on:input="onInput"
               @compositionend="onCompsitionEnd"
               v-bind:placeholder="strPlaceholder"
               autocomplete="off"
               @blur="onLeaveFocus">
    </template>
    <template v-else-if="false === enableNewLine">
        <input type="text"
               :value="value"
               v-on:input="onInput"
               @compositionend="onCompsitionEnd"
               v-bind:placeholder="strPlaceholder"
               autocomplete="off"
               @blur="onLeaveFocus">
    </template>
    <template v-else-if="false === disableResize">
        <textarea type="text"
                  :value="value"
                  v-on:input="onInput"
                  @compositionend="onCompsitionEnd"
                  v-bind:placeholder="strPlaceholder"
                  autocomplete="off"/>
    </template>
    <template v-else>
        <textarea type="text"
                  :value="value"
                  v-on:input="onInput"
                  @compositionend="onCompsitionEnd"
                  v-bind:placeholder="strPlaceholder"
                  autocomplete="off"
                  class="disableResize"/>
    </template>
</template>

<script lang="ts">
    import { Options, Vue } from "vue-class-component";
    import { ConverterIF } from "@/components/Input/Converter/ConverterIF";
    import HalfNumberConverter from "@/components/Input/Converter/HalfNumberConverter";
    import HalfNumberVersionConverter from "@/components/Input/Converter/HalfNumberVersionConverter";
    import StringConverter from "@/components/Input/Converter/StringConverter";
    import HalfAlphanumericSymbolConverter from "@/components/Input/Converter/HalfAlphanumericSymbolConverter";
    import SJISStringConverter from "@/components/Input/Converter/SJISStringConverter";
    import StringHalfNumberConverter from "@/components/Input/Converter/StringHalfNumberConverter";
    import DesignSJISStringConverter from "@/components/Input/Converter/DesignSJISStringConverter";
    import HalfAlphanumericSymbolProhibitSpaceConverter from "@/components/Input/Converter/HalfAlphanumericSymbolProhibitSpaceConverter";
    import HalfNumberCommaHyphenConverter from "@/components/Input/Converter/HalfNumberCommaHyphenConverter";
    import PackageNameConverter from "@/components/Input/Converter/PackageNameConverter";

    @Options({
        props: {
            // v-modelを二つ指定したときは、「v-model」が優先される。
            modelValue: { type: String, default: undefined },          // 受け取る変数<string>。「v-model」で指定する 
            numValue: { type: Number, default: undefined },            // 受け取る変数<number>。「v-model:numValue」で指定する

            convertType: { type: String, required: false },             // コンバーターの種類（定義はコンストラクタを参照）
            nullable: { type: Boolean, default: false },               // 「v-model」のnull許容（空文字のときに何を返すか）

            spinMode: { type: Boolean, default: false },               // スピンモード
            enableNewLine: { type: Boolean, default: false },          // 改行を許可するかどうか(許可する場合はtextareaが使用されます)
            disableResize: { type: Boolean, default: false },          // 改行許可時にリサイズを許可するかどうか

            designText: { type: String, required: false, default: "" },          // 入力制限

            strReplacePlaceholder: { type: String, required: false, default: "" },  // プレイスホルダー文字列変数(デフォルト文字から置き換える際に使用します)
        },
        emits: [
            'update:modelValue',     // v-modelの変更検知用
            'update:numValue',       // v-model:numValueの変更検知用
            'modifyText'                  // 内容変更ハンドラ
        ],
    })

    /**
     * inputに入力できる内容を制御するコンポーネント
     */
    export default class MaskInput extends Vue {
        // Props
        private modelValue!: string;    // 通知先（string）
        private numValue!: number;       // 通知先（number）
        private convertType!: string;   // コンバーターの種類
        private nullable!: boolean;   // null許容（空文字のときに何を返すか）
        private spinMode!: boolean;   // スピンモード
        private designText!: string;    // 入力制限
        private strReplacePlaceholder!: string;

        // メンバ変数
        private value = "";         // inputに表示されている値
        private strPlaceholder = "";
        private converter: ConverterIF = new StringConverter();

        /**
         * @brief	コントロール作成時に呼び出されます
         * @return	なし
         */
        public created(): void {
            // 文字変換処理を決定する
            switch (this.convertType) {
                case "half-alphanumeric-symbol":
                    // 半角英数字記号
                    this.converter = new HalfAlphanumericSymbolConverter();
                    this.strPlaceholder = "半角英数字記号";
                    break;
                case "half-number":
                    // 半角数字
                    this.converter = new HalfNumberConverter();
                    if ( !this.spinMode ){
                        this.strPlaceholder = "数値";
                    }
                    break;
                case "text":
                    // シンプルな文字列（制御コード除外）
                    // 初期値がStringConverterなので初期化不要
                    // this.converter = new StringConverter();
                    this.strPlaceholder = this.strReplacePlaceholder;
                    break;
                case "text-sjis":
                    // sjisの範囲内のみ入力可能
                    this.converter = new SJISStringConverter();
                    break;
                case "text-sjis-design":
                    // sjisの範囲内且つ指定文字のみ入力可能
                    this.converter = new DesignSJISStringConverter(this.designText);
                    break;
                case "string-harf-number":
                    // 半角数字の文字列(0から始まる数字を入力することができる。例：000123)
                    this.converter = new StringHalfNumberConverter();
                    break;
                case "half-number-version":
                    // 半角数字とピリオド(ブラウザバージョン文字列用)
                    this.converter = new HalfNumberVersionConverter();
                    this.strPlaceholder = "文字列";
                    break;
                case "half-alphanumeric-symbol-prohibit-space":
                    // 半角英数字記号(半角スペースを禁止する)
                    this.converter = new HalfAlphanumericSymbolProhibitSpaceConverter();
                    this.strPlaceholder = "半角英数字記号";
                    break;
                case "half-number-comma-hyphen":
                    // 半角数字とカンマ、ピリオド
                    this.converter = new HalfNumberCommaHyphenConverter();
                    break;
                case "package-name":
                    // 半角英数字、"."、"_"のみ入力可能
                    this.converter = new PackageNameConverter();
                    break;
                default:
                    // 初期値がStringConverterなので初期化不要
                    // this.converter = new StringConverter();
                    break;
            }

            // inputの初期値決定
            this.resetValue();
        }

        /**
         * @brief	beforeUpdate
         * @note    データが更新されたとき、DOM更新前
         * @return	なし
         */
        public beforeUpdate(): void {
            // inputの値を変更
            this.resetValue();
        }
        /**
         * @brief	onLeaveFocus
         * @note    フォーカスが外れた時
         * @return	なし
         */
        private onLeaveFocus(event: Event): void {
            //半角数字入力時のみ、フォーカスが外れた時に最小値があれば補正する。
            if (this.convertType == "half-number" && event.target instanceof HTMLInputElement && undefined !== this.numValue) {
                const nValue = parseInt(this.value);
                let value = this.value;
                let el = event.target as HTMLInputElement;
                const nMin = parseInt(el.min);
                if (isNaN(nValue)) {
                    if (false == isNaN(nMin)) {
                        this.$emit("update:numValue", nMin);
                        value = nMin.toString();
                    }
                    else {
                        if (!this.nullable) {
                            // min値がなければ0を設定します。
                            this.$emit("update:numValue", 0);
                            value = "0";
                        }
                    }
                } else if ( false == isNaN(nMin) && (nMin > nValue)) { //最小値以下の入力の場合は補正
  
                    this.$emit("update:numValue", nMin);
                    value = nMin.toString();          
                    
                    // min値がなければそのまま
                }
                this.value = value;
                

            }

        }
        /**
         * @brief	全角入力の変換処理が終了したときに呼び出されます。
         * @param   [in]    event   イベント
         * @return	なし
         */
        private onCompsitionEnd(event: Event): void {
            if (!(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
                return;
            }

            // inputイベントを発火し、補正処理を行う
            var e = new InputEvent("input");
            event.target.dispatchEvent(e);
        }

        /**
         * @brief	input要素に文字入力があったときに実行される処理
         * @param   [in]    event   イベント
         * @return	なし
         */
        private onInput(event: Event): void {
            if (event instanceof InputEvent) {
                if (event.inputType == "insertCompositionText") {
                    // 全角入力中は補正しない
                    // 全角入力後、onCompsitionEnd()でイベントを作成する
                    return;
                }
            }

            let el;
            if (event.target instanceof HTMLInputElement) {
                el = event.target as HTMLInputElement;
            }
            else if (event.target instanceof HTMLTextAreaElement) {
                el = event.target as HTMLTextAreaElement;
            }
            else {
                return;
            }

            // 最後のカーソル位置の判定に利用
            var nStartLength = el.value.length;

            // 現在のカーソル位置を記憶
            var nStart = el.selectionStart;
            if (null == nStart) {
                nStart = 0;
            }
            var nEnd = el.selectionEnd;
            if (null == nEnd) {
                nEnd = 0;
            }

            // コンバート処理
            let value = "";
            if (el instanceof HTMLInputElement) {
                value = this.converter.convertInputValue(el);
            }
            else if (el instanceof HTMLTextAreaElement) {
                value = this.converter.convertTextAreaValue(el);
            }
            if (null == value) {
                value = "";
            }

            // 「v-modal」で指定した変数を更新
            if (undefined !== this.modelValue) {
                this.$emit("update:modelValue", value);
            }
            // 「modal:numValue」で指定した変数を更新
            else if (undefined !== this.numValue && el instanceof HTMLInputElement) {
                const nValue = parseInt(value);
                if (isNaN(nValue)) {

                    // null許容
                    this.$emit("update:numValue", null);
                    value = "";
                }
                else {
                    this.$emit("update:numValue", nValue);
                }
            }

            // 値を反映
            el.value = value;
            this.value = value;

            // 文字が減っていたらカーソル位置も元に戻す
            // ※input type="number"の場合は警告が出るためカーソル位置の修正は不要
            if (!this.spinMode) {
                if (value.length < nStartLength) {
                    var nDeleteLength = nStartLength - value.length;
                    el.setSelectionRange(nStart - nDeleteLength, nEnd - nDeleteLength);
                }
                else {
                    // カーソル位置は変更なし(※Safariでカーソルが毎回後ろになってしまう件の修正)
                    el.setSelectionRange(nStart, nEnd);
                }
            }

            this.$emit('modifyText');
        }

        /**
         * @brief	resetValue
         * @note    最新のv-modelの値で表示内容をリセットします
         * @return	なし
         */
        private resetValue(): void {
            // inputの値を変更
            if (undefined !== this.modelValue) {
                if (null !== this.modelValue) {
                    this.value = this.modelValue;
                }
                else {
                    this.value = "";
                }
            }
            else if (undefined !== this.numValue) {
                if (null !== this.numValue) {
                    this.value = this.numValue.toString();
                }
                else {
                    this.value = "";
                }
            }
        }
    }
</script>

<style scoped>
    /* 入力欄にスピナーを常時表示する */
    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
        opacity: 1;
    }
    .disableResize {
        resize: none;
    }
</style>
